/**
 * 文章详情页 /article/:slug
 *
 * 设计：
 *  - 头图区：emoji 大图 + 标题 + 副标题 + 作者信息条
 *  - hero_quote：紫色引言条，强信号
 *  - 正文：react-markdown 渲染，移动端友好的中文排版
 *  - 文末：FollowOACard（阶段感知 CTA）
 *  - 文末下：同阶段相关阅读
 *  - 顶部固定一个"返回"按钮
 *
 * 排版要点：
 *  - 中文段落行高 1.7
 *  - h2/h3 增加上间距，与正文有节奏差
 *  - blockquote 渲染为"金句块"——大字号 + 紫色左边框 + 浅紫底
 *  - 列表项间距适中，移动端读起来不挤
 */
import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  loadArticle,
  listArticlesByStage,
  type Article,
  type ArticleMeta
} from '@/data/articles'
import { FollowOACard } from '@/components/FollowOACard'
import { track } from '@/lib/track'
import { setupWechatShare } from '@/lib/wechat-share'

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ok'; article: Article; related: ArticleMeta[] }
    | { status: 'not_found' }
  >({ status: 'loading' })

  useEffect(() => {
    if (!slug) {
      setState({ status: 'not_found' })
      return
    }
    let mounted = true
    ;(async () => {
      const a = await loadArticle(slug)
      if (!mounted) return
      if (!a) {
        setState({ status: 'not_found' })
        return
      }
      const primaryStage = a.meta.stage?.[0] ?? 'pre_exam'
      const sameStage = await listArticlesByStage(primaryStage)
      if (!mounted) return
      const related = sameStage.filter((m) => m.slug !== a.meta.slug).slice(0, 3)
      setState({ status: 'ok', article: a, related })
      track('article_open', {
        slug: a.meta.slug,
        category: a.meta.category,
        stage: primaryStage,
        free: a.meta.free
      })
      void setupWechatShare({
        title: a.meta.title,
        desc: a.meta.subtitle || a.meta.hero_quote || '方法星球 · 邹城家长公益项目'
      })
    })()
    return () => {
      mounted = false
    }
  }, [slug])

  if (state.status === 'loading') {
    return (
      <div className="container-mobile py-12 text-center text-ink-500">
        <div className="animate-pulse">加载中…</div>
      </div>
    )
  }

  if (state.status === 'not_found') {
    return (
      <div className="container-mobile py-12 text-center text-ink-600">
        <p>文章不存在或已下架。</p>
        <Link to="/" className="btn-ghost mt-4">
          返回首页
        </Link>
      </div>
    )
  }

  const { meta, body } = state.article
  const primaryStage = meta.stage?.[0] ?? 'pre_exam'
  const related = state.related

  return (
    <div className="min-h-screen bg-ink-50">
      {/* 顶部返回栏（粘性） */}
      <div className="sticky top-0 z-10 bg-ink-50/85 backdrop-blur border-b border-ink-100">
        <div className="container-mobile py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-ink-700 text-sm active:opacity-60"
          >
            <span>←</span>
            <span>返回</span>
          </button>
          <div className="text-xs text-ink-500">卓越方法星球</div>
        </div>
      </div>

      <article className="container-mobile py-6">
        {/* 头图区 */}
        <header className="mb-6">
          <div className="text-5xl mb-3">{meta.cover_emoji}</div>
          <h1 className="text-h1 text-ink-900 leading-tight mb-2">{meta.title}</h1>
          {meta.subtitle && (
            <p className="text-body text-ink-600 leading-relaxed">{meta.subtitle}</p>
          )}
          {/* 作者条 */}
          <div className="mt-4 pb-4 border-b border-ink-100 flex items-center gap-3 text-sm">
            <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-medium">
              {meta.author.slice(0, 1)}
            </div>
            <div className="flex-1">
              <div className="text-ink-800 font-medium">{meta.author}</div>
              <div className="text-ink-500 text-xs">{meta.author_title}</div>
            </div>
            <div className="text-right text-ink-500 text-xs leading-tight">
              <div>{meta.published_at}</div>
              <div className="mt-0.5">{meta.read_minutes} 分钟阅读</div>
            </div>
          </div>
        </header>

        {/* Hero quote */}
        {meta.hero_quote && (
          <div className="mb-6 px-4 py-4 bg-brand-50 border-l-4 border-brand-500 rounded-r-card">
            <div className="text-ink-800 leading-relaxed font-medium">
              「{meta.hero_quote}」
            </div>
          </div>
        )}

        {/* 正文 */}
        <div className="article-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: () => null, // markdown 里的第一个 H1 已在 header 渲染过，body 里隐藏
              h2: ({ children }) => (
                <h2 className="text-h2 text-ink-900 mt-8 mb-3 leading-snug">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-h3 text-ink-900 mt-6 mb-2 leading-snug">{children}</h3>
              ),
              p: ({ children }) => (
                <p className="text-body text-ink-800 leading-relaxed mb-4">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="my-4 pl-5 list-disc space-y-2 text-body text-ink-800 leading-relaxed marker:text-brand-400">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="my-4 pl-5 list-decimal space-y-2 text-body text-ink-800 leading-relaxed marker:text-brand-400">
                  {children}
                </ol>
              ),
              li: ({ children }) => <li className="pl-1">{children}</li>,
              blockquote: ({ children }) => (
                <blockquote className="my-5 px-4 py-3 bg-brand-50/60 border-l-4 border-brand-400 rounded-r-xl text-ink-800 leading-relaxed">
                  {children}
                </blockquote>
              ),
              strong: ({ children }) => (
                <strong className="text-ink-900 font-semibold">{children}</strong>
              ),
              em: ({ children }) => <em className="text-brand-700 not-italic font-medium">{children}</em>,
              hr: () => <hr className="my-8 border-ink-200" />,
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-700 underline underline-offset-2"
                >
                  {children}
                </a>
              ),
              code: ({ children }) => (
                <code className="px-1.5 py-0.5 bg-ink-100 text-ink-800 rounded text-sm">
                  {children}
                </code>
              )
            }}
          >
            {body}
          </ReactMarkdown>
        </div>

        {/* 文末 CTA */}
        <div className="mt-8">
          <FollowOACard stage={primaryStage} />
        </div>

        {/* 相关阅读 */}
        {related.length > 0 && (
          <section className="mt-8">
            <h3 className="section-title">相关阅读</h3>
            <div className="space-y-2">
              {related.map((m) => (
                <Link
                  key={m.slug}
                  to={`/article/${m.slug}`}
                  className="block card hover:shadow-md transition active:scale-[0.99]"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl shrink-0">{m.cover_emoji}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-ink-900 font-medium leading-snug truncate">
                        {m.title}
                      </div>
                      {m.subtitle && (
                        <div className="text-ink-500 text-xs mt-0.5 truncate">
                          {m.subtitle}
                        </div>
                      )}
                    </div>
                    <span className="text-brand-500 shrink-0">→</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* 来源说明 */}
        {meta.source_note && (
          <div className="mt-8 text-[11px] text-ink-400 leading-relaxed">
            ※ {meta.source_note}
          </div>
        )}

        {/* 底部返回首页 */}
        <div className="mt-10 pb-4 text-center">
          <Link to="/" className="btn-ghost">
            返回首页
          </Link>
        </div>
      </article>
    </div>
  )
}
